import { getUrlFromFetch, getHeadersFromFetch, getFetchMethod, addHeadersToFetchRequest, isServerAction, getFetchBody, isGraphqlError, buildGraphqlError, isHttpError, buildHttpError, getBodyFromXhrSend, getUrlFromXhr, getXhrResponseHeaders } from './chunk-RDMEGTFF.js';
import { shouldIgnoreUrl } from './chunk-5E3LDJWR.js';
import { buildContext, buildLog, augmentTrackingHeaders, buildExecAppelRest } from './chunk-JPNTSM5L.js';
import { v4 } from './chunk-CW6A3IUB.js';
import Debug from '@bytel/debug';
export { Debug } from '@bytel/debug';

var debug = Debug("logger:PrismeLogger");
var PrismeLogger = class {
  config;
  transporter;
  __name;
  constructor(config, transporter, autoInstrumentationCtx) {
    this.__name = `PrismeLogger_${v4()}`;
    this.config = config;
    this.transporter = transporter;
    debug(`creating PrismeLogger ${this.__name}`, { config });
    if (config.useAutoInstrumentation) {
      createCoreAutoInstrumentation(this, autoInstrumentationCtx);
    }
  }
  logToPrisme(message, {
    erreur,
    action,
    severity,
    logName,
    ctx = {},
    ...specifiqueMetier
  }) {
    try {
      const currentTime = Date.now();
      const mergedCtx = {
        ...buildContext(this.config, { specifiqueMetier }),
        ...ctx
      };
      const log = buildLog({
        config: this.config,
        currentTime,
        message,
        erreur,
        action,
        severity,
        logName
      });
      this.transporter.push({
        ctx: mergedCtx,
        events: [log]
      });
    } catch (error) {
      console.error("Erreur lors de l'envoi du message dans prisme", error);
    }
  }
  getConfig() {
    return this.config;
  }
  getTransporter() {
    return this.transporter;
  }
  log(logName, message, ctx) {
    this.logToPrisme(message, { logName, severity: "INFO", ctx });
  }
  debug(logNameOrMessage, messageOrContext, context) {
    const _debug = Debug("debug", this.log);
    if (typeof messageOrContext === "string") {
      _debug(logNameOrMessage, messageOrContext, context);
      console.log(logNameOrMessage, messageOrContext);
    } else {
      _debug("DEBUG", logNameOrMessage, messageOrContext);
      console.log("DEBUG", logNameOrMessage);
    }
  }
  error(err, ctx) {
    this.logToPrisme(err.message, {
      erreur: err,
      severity: "ERROR",
      ctx
    });
  }
  async flush() {
    debug("flush");
    return await this.transporter.send();
  }
};
var debug2 = Debug("logger:core-auto-instrumentation");
var originalFetch = globalThis.fetch;
var createCoreAutoInstrumentation = (logger, customCtx) => {
  if ("onerror" in globalThis) {
    globalThis.addEventListener("error", function(data) {
      if ("filename" in data) {
        logger.error({
          type: "FONCTIONNELLE",
          code: "ON_ERROR",
          origine: data.filename ?? "unknown",
          message: data.message,
          description: data.error?.toString() ?? ""
        });
      } else {
        console.warn("unhandled error event", data);
      }
    });
  }
  if ("onunhandledrejection" in globalThis) {
    globalThis.addEventListener(
      "unhandledrejection",
      function(event) {
        let errorString;
        if (event.reason instanceof Error) {
          errorString = event.reason.toString();
        } else if (typeof event.reason === "string") {
          errorString = event.reason;
        } else {
          try {
            errorString = JSON.stringify(errorString);
          } catch {
            console.error(event.reason);
            errorString = "log is not string compatible";
          }
        }
        logger.log("ON_UNHANDLED_REJECTION", errorString);
      }
    );
  }
  if ("fetch" in globalThis) {
    globalThis.fetch = originalFetch;
    globalThis.fetch = async (...args) => {
      const config = logger.getConfig();
      let url = getUrlFromFetch(args[0]);
      if (url === config.urlEndpoint || shouldIgnoreUrl(url, config)) {
        debug2("Ignoring url", url, "args", args);
        return await originalFetch(...args);
      }
      const requestHeaders = getHeadersFromFetch(...args);
      const trackerId = typeof window !== "undefined" ? customCtx?.trackerIdProvider?.() ?? config.getTrackerId?.() : requestHeaders?.trackerid ?? v4();
      const augmentedRequestHeaders = augmentTrackingHeaders(
        config.st,
        trackerId,
        requestHeaders
      );
      const method = getFetchMethod(...args);
      const beforeFetchTime = Date.now();
      addHeadersToFetchRequest(args[0], args[1], augmentedRequestHeaders);
      const response = await originalFetch(...args);
      const cloned = response.clone();
      const contentType = cloned.headers.get("content-type");
      if (isServerAction(url, contentType)) {
        url = window.location.href;
      }
      const body = await getFetchBody(...args);
      let data;
      try {
        if (contentType && contentType.includes("application/json")) {
          data = await cloned.json();
        } else {
          data = await cloned.text();
        }
      } catch (error2) {
        data = {};
      }
      let error;
      if (isGraphqlError(body, data)) {
        error = buildGraphqlError(data.errors);
      } else if (isHttpError(cloned.status)) {
        error = buildHttpError(data, cloned.status);
      }
      const execAppelRest = buildExecAppelRest(
        config,
        beforeFetchTime,
        requestHeaders,
        //
        url,
        method,
        body,
        cloned.status,
        error
      );
      const ctx = buildContext(config, {
        headers: requestHeaders,
        userInfoProvider: customCtx?.userInfoProvider ?? config?.userInfoProvider
      });
      logger.getTransporter().push({
        ctx,
        events: [execAppelRest]
      });
      return response;
    };
  }
  if ("XMLHttpRequest" in globalThis) {
    const xhrproto = XMLHttpRequest.prototype;
    xhrproto.send = new Proxy(xhrproto.send, {
      apply(originalSend, xhrSendThisArg, xhrSendArgsList) {
        xhrSendThisArg.body = getBodyFromXhrSend(xhrSendArgsList[0]);
        return originalSend.apply(xhrSendThisArg, xhrSendArgsList);
      }
    });
    xhrproto.open = new Proxy(xhrproto.open, {
      apply(originalOpen, xhrOpenThisArg, xhrOpenArgsList) {
        const beforeFetchTime = Date.now();
        const config = logger.getConfig();
        const requestHeaders = {};
        const trackingHeaders = augmentTrackingHeaders(
          config.st,
          config.getTrackerId?.(),
          {}
        );
        if (xhrOpenArgsList[1] === config.urlEndpoint || shouldIgnoreUrl(getUrlFromXhr(xhrOpenArgsList[1]), config)) {
          return originalOpen.apply(xhrOpenThisArg, xhrOpenArgsList);
        }
        xhrOpenThisArg.setRequestHeader = new Proxy(
          xhrOpenThisArg.setRequestHeader,
          {
            apply(originalSetRequestHeader, setRequestHeaderThisArg, setRequestHeaderArgArray) {
              if (setRequestHeaderArgArray[0] && setRequestHeaderArgArray[1]) {
                requestHeaders[setRequestHeaderArgArray[0]] = setRequestHeaderArgArray[1];
              }
              return originalSetRequestHeader.apply(
                setRequestHeaderThisArg,
                setRequestHeaderArgArray
              );
            }
          }
        );
        const onreadystatechangeHandler = () => {
          if (xhrOpenThisArg.readyState === xhrOpenThisArg.DONE) {
            const responseHeaders = getXhrResponseHeaders(xhrOpenThisArg);
            const method = xhrOpenArgsList[0];
            const url = getUrlFromXhr(xhrOpenArgsList[1]);
            const data = xhrOpenThisArg.body;
            let error;
            if (isGraphqlError(data, xhrOpenThisArg.response)) {
              error = buildGraphqlError(xhrOpenThisArg.response.errors);
            } else if (isHttpError(xhrOpenThisArg.status)) {
              try {
                error = buildHttpError(
                  JSON.parse(xhrOpenThisArg.response),
                  xhrOpenThisArg.status
                );
              } catch {
                error = buildHttpError(
                  xhrOpenThisArg.response,
                  xhrOpenThisArg.status
                );
              }
            }
            const execAppelRest = buildExecAppelRest(
              config,
              beforeFetchTime,
              requestHeaders,
              url,
              method,
              data,
              xhrOpenThisArg.status,
              error
            );
            const ctx = buildContext(config, {
              headers: requestHeaders,
              userInfoProvider: customCtx?.userInfoProvider ?? config?.userInfoProvider
            });
            logger.getTransporter().push({
              ctx,
              events: [execAppelRest]
            });
            debug2({ data, url, method, responseHeaders });
          }
        };
        if ("onreadystatechange" in xhrOpenThisArg && typeof xhrOpenThisArg.onreadystatechange === "function") {
          xhrOpenThisArg.onreadystatechange = new Proxy(
            xhrOpenThisArg.onreadystatechange,
            {
              apply(originalOnreadystatechange, onreadystatechangeThisArg, onreadystatechangeArgArray) {
                onreadystatechangeHandler();
                return originalOnreadystatechange.apply(
                  onreadystatechangeThisArg,
                  onreadystatechangeArgArray
                );
              }
            }
          );
        } else {
          xhrOpenThisArg.addEventListener(
            "readystatechange",
            onreadystatechangeHandler
          );
        }
        const opened = originalOpen.apply(xhrOpenThisArg, xhrOpenArgsList);
        Object.keys(trackingHeaders).forEach((headerName) => {
          if (headerName && trackingHeaders[headerName]) {
            xhrOpenThisArg.setRequestHeader(
              headerName,
              trackingHeaders[headerName]
            );
          }
        });
        return opened;
      }
    });
  }
};

export { PrismeLogger, createCoreAutoInstrumentation };
//# sourceMappingURL=chunk-CXV2RZKI.js.map
//# sourceMappingURL=chunk-CXV2RZKI.js.map